// §26 Set up your services — net banking, UPI, debit card, cheque book and
// alerts, saved in one call to POST /onboarding/applications/{id}/digital-banking.
// The GET prefills when the customer revisits.
import { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router';
import { ApiError } from '@/api/client';
import { getApplicantHome } from '@/api/applicant';
import { saveDigitalBanking, type DigitalBankingData } from '@/api/digitalBanking';
import { getSession } from '@/api/session';
import './onboarding.css';

const CARD_VARIANTS = ['CLASSIC', 'PLATINUM'];
const CARD_TYPES: Array<{ value: string; label: string }> = [
  { value: 'VIRTUAL', label: 'Virtual card' },
  { value: 'PHYSICAL', label: 'Physical card' },
];
const CHEQUE_LEAVES = [25, 50, 100];

const DEFAULTS: DigitalBankingData = {
  digitalEnabled: true,
  secondFactor: 'OTP',
  upiEnabled: false,
  upiId: null,
  cardVariant: null,
  cardType: null,
  nameOnCard: null,
  cardDelivery: null,
  collectionBranch: null,
  domesticAtm: true,
  domesticPos: true,
  domesticOnline: true,
  contactless: false,
  international: false,
  atmLimitRupees: undefined,
  posLimitRupees: undefined,
  wantsChequeBook: false,
  chequeLeaves: undefined,
  chequeName: null,
  whatsappAlerts: false,
  eStatement: true,
  statementFrequency: 'MONTHLY',
  physicalStatement: false,
};

const NetBanking = () => {
  const navigate = useNavigate();
  const session = getSession();
  const [applicationId, setApplicationId] = useState<string | null>(null);
  const [branchName, setBranchName] = useState('');
  const [form, setForm] = useState<DigitalBankingData>(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!session?.reference) return;
    let cancelled = false;
    getApplicantHome(session.reference)
      .then((home) => {
        if (cancelled) return;
        setApplicationId(home.application.applicationId);
        setBranchName(home.application.branchName ?? '');
        if (home.digitalBanking) setForm({ ...DEFAULTS, ...home.digitalBanking });
      })
      .catch(() => {
        if (!cancelled) setError('Could not load your services. Please try again.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = <K extends keyof DigitalBankingData>(key: K, value: DigitalBankingData[K]) => {
    setForm((f) => ({ ...f, [key]: value }));
    setError('');
  };

  const handleSave = async () => {
    if (!applicationId) return;
    if (form.upiEnabled && form.upiId && !/^[\w.-]+@[a-zA-Z]+$/.test(form.upiId)) {
      setError('That UPI ID does not look right — it should look like name@bank.');
      return;
    }
    if (form.wantsChequeBook && !form.chequeName?.trim()) {
      setError('Enter the name to print on the cheque book.');
      return;
    }
    setSaving(true);
    try {
      await saveDigitalBanking(applicationId, form);
      navigate('/applicant', { state: { netBankingSaved: true } });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save your services. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (!session?.reference) {
    return <Navigate to="/login" replace />;
  }

  if (loading) {
    return (
      <div className="nova-ob-page">
        <span className="spinner-border text-primary" aria-hidden="true" />
      </div>
    );
  }

  if (!applicationId) {
    return (
      <div className="nova-ob-page">
        <div className="nova-ob-panel nova-ob-panel-narrow text-center">
          <p className="text-muted mb-3">{error || 'No application found.'}</p>
          <button type="button" className="btn btn-primary" onClick={() => navigate('/applicant')}>
            Back to home
          </button>
        </div>
      </div>
    );
  }

  const wantsCard = !!form.cardType;

  return (
    <div className="nova-ob-page">
      <div className="nova-ob-header" style={{ maxWidth: 720 }}>
        <button type="button" className="nova-ob-back mb-2" aria-label="Back" onClick={() => navigate('/applicant')}>
          ←
        </button>
        <h1>Set up your services</h1>
        <p>Choose what you want switched on for this account. You can change these later.</p>
      </div>

      <div className="nova-ob-body" style={{ maxWidth: 720, paddingTop: '1.5rem' }}>
        {/* Net banking */}
        <div className="nova-ob-section">
          <h2 className="nova-ob-section-title">Net banking</h2>
          <div className="nova-ob-panel">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <span className="nova-ob-label mb-0">Full digital banking</span>
              <div className="form-check form-switch m-0">
                <input
                  className="form-check-input"
                  type="checkbox"
                  role="switch"
                  checked={form.digitalEnabled}
                  onChange={(e) => set('digitalEnabled', e.target.checked)}
                  aria-label="Full digital banking"
                />
              </div>
            </div>
            {form.digitalEnabled && (
              <>
                <label className="nova-ob-label" htmlFor="nb-2fa">Second factor for transactions</label>
                <select
                  id="nb-2fa"
                  className="form-select"
                  value={form.secondFactor}
                  onChange={(e) => set('secondFactor', e.target.value)}
                >
                  <option value="OTP">OTP on registered mobile</option>
                  <option value="MPIN">MPIN</option>
                </select>
              </>
            )}
          </div>
        </div>

        {/* UPI */}
        <div className="nova-ob-section">
          <h2 className="nova-ob-section-title">UPI</h2>
          <div className="nova-ob-panel">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <span className="nova-ob-label mb-0">Enable UPI payments</span>
              <div className="form-check form-switch m-0">
                <input
                  className="form-check-input"
                  type="checkbox"
                  role="switch"
                  checked={form.upiEnabled}
                  onChange={(e) => set('upiEnabled', e.target.checked)}
                  aria-label="Enable UPI"
                />
              </div>
            </div>
            {form.upiEnabled && (
              <>
                <label className="nova-ob-label" htmlFor="nb-upi-id">Your UPI ID (optional)</label>
                <input
                  id="nb-upi-id"
                  className="form-control"
                  placeholder="name@bank"
                  value={form.upiId ?? ''}
                  onChange={(e) => set('upiId', e.target.value || null)}
                />
                <div className="form-text">Leave blank and we will create one from your mobile number.</div>
              </>
            )}
          </div>
        </div>

        {/* Debit card */}
        <div className="nova-ob-section">
          <h2 className="nova-ob-section-title">Debit card</h2>
          <div className="nova-ob-panel">
            <label className="nova-ob-label">Card type</label>
            <div className="nova-ob-seg mb-3">
              {CARD_TYPES.map((t) => (
                <button
                  key={t.value}
                  type="button"
                  className={form.cardType === t.value ? 'selected' : ''}
                  onClick={() => set('cardType', form.cardType === t.value ? null : t.value)}
                >
                  {t.label}
                </button>
              ))}
            </div>

            {wantsCard && (
              <>
                <div className="row g-3 mb-3">
                  <div className="col-md-6">
                    <label className="nova-ob-label" htmlFor="nb-card-variant">Variant</label>
                    <select
                      id="nb-card-variant"
                      className="form-select"
                      value={form.cardVariant ?? 'CLASSIC'}
                      onChange={(e) => set('cardVariant', e.target.value)}
                    >
                      {CARD_VARIANTS.map((v) => (
                        <option key={v} value={v}>
                          {v.charAt(0) + v.slice(1).toLowerCase()}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-6">
                    <label className="nova-ob-label" htmlFor="nb-card-name">Name on card</label>
                    <input
                      id="nb-card-name"
                      className="form-control"
                      placeholder="As it should appear"
                      value={form.nameOnCard ?? ''}
                      onChange={(e) => set('nameOnCard', e.target.value || null)}
                    />
                  </div>
                </div>

                {form.cardType === 'PHYSICAL' && (
                  <>
                    <label className="nova-ob-label">Delivery</label>
                    <div className="nova-ob-seg mb-3">
                      <button
                        type="button"
                        className={form.cardDelivery !== 'BRANCH' ? 'selected' : ''}
                        onClick={() => set('cardDelivery', 'HOME')}
                      >
                        Deliver to my address
                      </button>
                      <button
                        type="button"
                        className={form.cardDelivery === 'BRANCH' ? 'selected' : ''}
                        onClick={() => set('cardDelivery', 'BRANCH')}
                      >
                        Collect at branch
                      </button>
                    </div>
                    {form.cardDelivery === 'BRANCH' && (
                      <input
                        className="form-control mb-3"
                        placeholder="Branch"
                        value={form.collectionBranch ?? branchName}
                        onChange={(e) => set('collectionBranch', e.target.value || null)}
                        aria-label="Collection branch"
                      />
                    )}
                  </>
                )}

                <label className="nova-ob-label">Where the card works</label>
                <div className="row g-2 mb-3">
                  {(
                    [
                      ['domesticAtm', 'ATM (India)'],
                      ['domesticPos', 'Shops (India)'],
                      ['domesticOnline', 'Online (India)'],
                      ['contactless', 'Contactless'],
                      ['international', 'International'],
                    ] as const
                  ).map(([key, label]) => (
                    <div className="col-6" key={key}>
                      <div className="form-check">
                        <input
                          id={`nb-${key}`}
                          className="form-check-input"
                          type="checkbox"
                          checked={!!form[key]}
                          onChange={(e) => set(key, e.target.checked)}
                        />
                        <label className="form-check-label" htmlFor={`nb-${key}`}>
                          {label}
                        </label>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="nova-ob-label" htmlFor="nb-atm-limit">Daily ATM limit (₹)</label>
                    <input
                      id="nb-atm-limit"
                      type="number"
                      min={0}
                      className="form-control"
                      placeholder="e.g. 25000"
                      value={form.atmLimitRupees ?? ''}
                      onChange={(e) => set('atmLimitRupees', e.target.value ? Number(e.target.value) : undefined)}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="nova-ob-label" htmlFor="nb-pos-limit">Daily spend limit (₹)</label>
                    <input
                      id="nb-pos-limit"
                      type="number"
                      min={0}
                      className="form-control"
                      placeholder="e.g. 50000"
                      value={form.posLimitRupees ?? ''}
                      onChange={(e) => set('posLimitRupees', e.target.value ? Number(e.target.value) : undefined)}
                    />
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Cheque book */}
        <div className="nova-ob-section">
          <h2 className="nova-ob-section-title">Cheque book</h2>
          <div className="nova-ob-panel">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <span className="nova-ob-label mb-0">Send me a cheque book</span>
              <div className="form-check form-switch m-0">
                <input
                  className="form-check-input"
                  type="checkbox"
                  role="switch"
                  checked={form.wantsChequeBook}
                  onChange={(e) => set('wantsChequeBook', e.target.checked)}
                  aria-label="Cheque book"
                />
              </div>
            </div>
            {form.wantsChequeBook && (
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="nova-ob-label" htmlFor="nb-cheque-name">Name on cheque</label>
                  <input
                    id="nb-cheque-name"
                    className="form-control"
                    value={form.chequeName ?? ''}
                    onChange={(e) => set('chequeName', e.target.value || null)}
                  />
                </div>
                <div className="col-md-6">
                  <label className="nova-ob-label" htmlFor="nb-cheque-leaves">Leaves</label>
                  <select
                    id="nb-cheque-leaves"
                    className="form-select"
                    value={form.chequeLeaves ?? 25}
                    onChange={(e) => set('chequeLeaves', Number(e.target.value))}
                  >
                    {CHEQUE_LEAVES.map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Alerts & statements */}
        <div className="nova-ob-section">
          <h2 className="nova-ob-section-title">Alerts & statements</h2>
          <div className="nova-ob-panel">
            {(
              [
                ['whatsappAlerts', 'WhatsApp alerts'],
                ['eStatement', 'Email statements'],
                ['physicalStatement', 'Paper statements by post'],
              ] as const
            ).map(([key, label]) => (
              <div className="d-flex justify-content-between align-items-center mb-3" key={key}>
                <span className="nova-ob-label mb-0">{label}</span>
                <div className="form-check form-switch m-0">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    role="switch"
                    checked={!!form[key]}
                    onChange={(e) => set(key, e.target.checked)}
                    aria-label={label}
                  />
                </div>
              </div>
            ))}
            {form.eStatement && (
              <>
                <label className="nova-ob-label" htmlFor="nb-statement-freq">Statement frequency</label>
                <select
                  id="nb-statement-freq"
                  className="form-select"
                  value={form.statementFrequency ?? 'MONTHLY'}
                  onChange={(e) => set('statementFrequency', e.target.value)}
                >
                  <option value="MONTHLY">Monthly</option>
                  <option value="QUARTERLY">Quarterly</option>
                </select>
              </>
            )}
          </div>
        </div>

        {error && (
          <div className="alert alert-danger py-2" role="alert">
            {error}
          </div>
        )}

        <div className="nova-ob-footer">
          <div className="nova-ob-footer-inner">
            <button
              type="button"
              className="btn btn-primary btn-lg fw-semibold"
              disabled={saving}
              onClick={handleSave}
            >
              {saving && <span className="spinner-border spinner-border-sm me-2" aria-hidden="true" />}
              Save services
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NetBanking;
